import { diffLines } from "diff";

export type ParsedDiff = {
    patch: string;
    stats: {
        additions: number;
        deletions: number;
    };
};

type DiffGroup = {
    type: "added" | "removed" | "unchanged";
    lines: string[];
};

/**
 * Build a git-format diff patch from structured DiffGroups.
 * Uses "diff --git" header so @pierre/diffs parses it with
 * GIT_DIFF_FILE_BREAK_REGEX, avoiding false file-boundary matches
 * when diff body lines start with "---" (e.g. removed SQL comments).
 */
export const convertDiffGroupsToPatch = (
    filePath: string,
    startLine: number,
    diffGroups: DiffGroup[],
    stats: ParsedDiff["stats"],
): ParsedDiff => {
    const patchLines: string[] = [`diff --git a/${filePath} b/${filePath}`, `--- a/${filePath}`, `+++ b/${filePath}`];

    let oldLineCount = 0;
    let newLineCount = 0;
    const bodyLines: string[] = [];

    for (const group of diffGroups) {
        for (const line of group.lines) {
            if (group.type === "removed") {
                bodyLines.push(`-${line}`);
                oldLineCount++;
            } else if (group.type === "added") {
                bodyLines.push(`+${line}`);
                newLineCount++;
            } else {
                bodyLines.push(` ${line}`);
                oldLineCount++;
                newLineCount++;
            }
        }
    }

    patchLines.push(`@@ -${startLine},${oldLineCount} +${startLine},${newLineCount} @@`);
    patchLines.push(...bodyLines);

    return {
        patch: patchLines.join("\n"),
        stats,
    };
};

/**
 * Parse legacy SEARCH/REPLACE content into a unified diff patch string.
 */
export const parseDiffContent = (content: string, filePath: string, startLine: number): ParsedDiff => {
    const searchMarker = "<<<<<<< SEARCH";
    const separatorMarker = "=======";
    const replaceMarker = ">>>>>>> REPLACE";

    const searchStart = content.indexOf(searchMarker);
    const separatorStart = content.indexOf(separatorMarker);
    const replaceEnd = content.indexOf(replaceMarker);

    if (searchStart === -1 || separatorStart === -1 || replaceEnd === -1) {
        return {
            patch: "",
            stats: { additions: 0, deletions: 0 },
        };
    }

    const oldContent = content.slice(searchStart + searchMarker.length, separatorStart).trim();
    const newContent = content.slice(separatorStart + separatorMarker.length, replaceEnd).trim();

    let additions = 0;
    let deletions = 0;
    const bodyLines: string[] = [];

    if (oldContent || newContent) {
        const normalize = (text: string) => (text.endsWith("\n") ? text : text + "\n");
        const changes = diffLines(normalize(oldContent), normalize(newContent));
        for (const change of changes) {
            const lines = change.value.split("\n");
            // diffLines produces a trailing empty string after the final newline
            const trimmed = lines.length > 0 && lines[lines.length - 1] === "" ? lines.slice(0, -1) : lines;
            for (const line of trimmed) {
                if (change.added) {
                    bodyLines.push(`+${line}`);
                    additions++;
                } else if (change.removed) {
                    bodyLines.push(`-${line}`);
                    deletions++;
                } else {
                    bodyLines.push(` ${line}`);
                }
            }
        }
    }

    const oldLineCount = bodyLines.filter((l) => l.startsWith("-") || l.startsWith(" ")).length;
    const newLineCount = bodyLines.filter((l) => l.startsWith("+") || l.startsWith(" ")).length;

    const patchLines = [
        `diff --git a/${filePath} b/${filePath}`,
        `--- a/${filePath}`,
        `+++ b/${filePath}`,
        `@@ -${startLine},${oldLineCount} +${startLine},${newLineCount} @@`,
        ...bodyLines,
    ];

    return {
        patch: patchLines.join("\n"),
        stats: { additions, deletions },
    };
};
